/**
 * NarrativeBlock — editorial pull-quote treatment for the AI-authored
 * executive narrative paragraph.
 *
 * The narrative comes back from Azure OpenAI as a freeform paragraph (or
 * paragraphs). Rather than dropping it into a generic card, we frame it like
 * the editor's note that closes a magazine feature: ALL CAPS kicker, italic
 * deck (deterministic, not AI-generated), then the narrative body in a
 * larger reading column with a tracked-uppercase byline strip below.
 *
 * Empty handling: when the narrative is absent (Azure OpenAI failure, missing
 * key, etc.) we still render the section with a quiet placeholder — the
 * executive shouldn't be left wondering whether the section was meant to
 * appear at all.
 */

interface Props {
  narrative: string | null
  /** Italic deck above the narrative — composed deterministically by the
   *  brief composer, not the AI. Frames the section in Guardian's voice. */
  deck: string
  /** Tenant name for the byline ("Composed for {tenantName}"). */
  tenantName: string
  /** Reporting window in days — included in the byline period chip. */
  periodDays: number
}

export default function NarrativeBlock({ narrative, deck, tenantName, periodDays }: Props) {
  const hasNarrative = typeof narrative === 'string' && narrative.trim().length > 0

  return (
    <section className="mt-12">
      <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">
        Executive Brief · By Guardian
      </div>
      <h2
        className="mt-1 font-display font-extrabold tracking-[-0.018em] text-[28px]"
        style={{ color: 'rgb(var(--color-text-primary))' }}
      >
        The board-ready{' '}
        <em
          style={{
            fontStyle: 'italic',
            fontWeight: 600,
            color: 'rgb(var(--color-primary-700))',
          }}
        >
          read.
        </em>
      </h2>
      <p
        className="mt-2 max-w-2xl text-[14px] italic"
        style={{ color: 'rgb(var(--color-text-secondary))' }}
      >
        {deck}
      </p>

      <article
        className="
          mt-6 max-w-3xl rounded-lg border bg-dark-800 p-8
        "
        style={{ borderColor: 'rgb(var(--color-dark-600))' }}
      >
        {hasNarrative ? (
          <p
            className="whitespace-pre-wrap text-[15px] leading-[1.7]"
            style={{ color: 'rgb(var(--color-text-primary))' }}
          >
            {narrative}
          </p>
        ) : (
          <p
            className="text-[14px] italic"
            style={{ color: 'rgb(var(--color-text-secondary))' }}
          >
            Guardian couldn’t compose a narrative for this period. The
            numbers above tell the story; check back after the next refresh.
          </p>
        )}

        <div
          className="mt-6 flex flex-wrap gap-3 border-t pt-3 text-[10px] font-bold uppercase tracking-[0.18em]"
          style={{
            borderTopColor: 'rgb(var(--color-dark-600))',
            color: 'rgb(var(--color-text-secondary))',
          }}
        >
          <span>Composed by Guardian</span>
          <span aria-hidden>·</span>
          <span>For {tenantName}</span>
          <span aria-hidden>·</span>
          <span>{periodDays}-day period</span>
        </div>
      </article>
    </section>
  )
}
